import type { ValidationRule, ValidationValueMessage } from 'react-hook-form';

const isRequired: ValidationRule<boolean> = {
    value: true,
    message: 'This field is required'
};

const REGEX = {
    LOWERCASE: '[a-z]',
    UPPERCASE: '[A-Z]',
    NUMBER: '\\d',
    SPECIAL: '[^A-Za-z0-9\\s_]'
};

const isEmail: ValidationRule<RegExp> = {
    value:
        // eslint-disable-next-line no-control-regex
        /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/i,
    message: 'Please enter an valid email'
};

export const validators = {
    isRequired,
    isEmail
};

export enum PasswordRequirementTypes {
    Lowercase = 'lowercase letter',
    Uppercase = 'uppercase letter',
    Numbers = 'number',
    Special = 'special character',
    Complexity = 'complexity'
}

/**
 *
 * @function formatPluralString  to return strings in singular or plural form
 * @returns
 */
export const formatPluralString = (value: number, statement: string, isRange?: boolean) => {
    const endofstatement = isRange ? (statement === '' ? '' : '.') : ', ';

    if (value === 0) {
        return '';
    }

    if (value === 1 || statement === '') {
        return `${value} ${statement}${endofstatement}`;
    } else {
        return `${value} ${statement}s${endofstatement}`;
    }
};

export type PasswordRequirementsProps = {
    validationExpression: ValidationValueMessage<RegExp>;
    description: string;
};

/**
 *
 * @function getPasswordValidations Get password validation based on API response
 * @returns
 */
export const getPasswordValidations = (passwordRequirements): PasswordRequirementsProps => {
    let alphaNumericPassword = '^';
    const descriptionParts: string[] = [];

    if (passwordRequirements.length) {
        let minLength: number | null = null;
        let maxLength: number | null = null;

        // Collect rules
        passwordRequirements.forEach((requirement) => {
            const value = parseInt(requirement.value);
            const { description, expression } = createPasswordValidation(requirement.name, value);

            if (requirement.name.includes('min-length')) {
                minLength = value;
                return;
            }

            if (requirement.name.includes('max-length')) {
                maxLength = value;
                return;
            }

            if (value > 0) {
                // Add regex
                alphaNumericPassword += expression;
            }

            // Show only requirements with value > 0
            if (value > 0 && description.trim() !== '') {
                descriptionParts.push(description.replace(/,\s*$/, ''));
            }
        });

        // Handle length rule
        if (minLength !== null) {
            if (maxLength !== null) {
                alphaNumericPassword += `.{${minLength},${maxLength}}$`;
                descriptionParts.push(`from ${minLength} to ${maxLength} characters`);
            } else {
                alphaNumericPassword += `.{${minLength},}$`;
                descriptionParts.push(`at least ${minLength} characters`);
            }
        } else if (maxLength !== null && minLength === null) {
            alphaNumericPassword += `.{0,${maxLength}}$`;
            descriptionParts.push(`at most ${maxLength} characters`);
        }

        // Build description with proper grammar
        let finalDescription = '';

        if (descriptionParts.length === 1) {
            finalDescription += descriptionParts[0] + '.';
        } else if (descriptionParts.length > 1) {
            finalDescription += descriptionParts.join(', ') + '.';
        }

        return {
            validationExpression: {
                value: new RegExp(alphaNumericPassword),
                message: 'Please enter the correct password format'
            },
            description: finalDescription
        };
    }

    // Default fallback
    return {
        validationExpression: {
            value: /.*/, // matches anything
            message: 'Please enter the correct password format'
        },
        description: ''
    };
};

type createPasswordReturn = {
    expression: string;
    description: string;
};

/**
 *
 * @function createPasswordValidation Create password validation based on API response. except for min and max length which
 * will be declared individually
 * @returns
 */

export function createPasswordValidation(name: string, value: number): createPasswordReturn {
    if (value <= 0) {
        // Skip if no requirement
        return { expression: '', description: '' };
    }

    if (name.includes('uppercase')) {
        return {
            expression: `(?=(?:.*${REGEX.UPPERCASE}){${value}})`, // at least `value` uppercase letters
            description: `at least ${formatPluralString(value, PasswordRequirementTypes.Uppercase)}`
        };
    } else if (name.includes('lowercase')) {
        return {
            expression: `(?=(?:.*${REGEX.LOWERCASE}){${value}})`, // at least `value` lowercase letters
            description: `at least ${formatPluralString(value, PasswordRequirementTypes.Lowercase)}`
        };
    } else if (name.includes('number')) {
        return {
            expression: `(?=(?:.*${REGEX.NUMBER}){${value}})`, // at least `value` digits
            description: `at least ${formatPluralString(value, PasswordRequirementTypes.Numbers)}`
        };
    } else if (name.includes('special')) {
        return {
            expression: `(?=(?:.*${REGEX.SPECIAL}){${value}})`,
            description: `at least ${formatPluralString(value, PasswordRequirementTypes.Special)}`
        };
    } else if (name.includes('complexity')) {
        return {
            expression: '', // complexity is calculated separately
            description: `must have a minimum complexity level of ${value}`
        };
    } else {
        return {
            description: '',
            expression: ''
        };
    }
}

const countMatches = (value: string, regex: RegExp) => (value.match(regex) || []).length;

const getMinFromDescription = (description: string, keyword: string) => {
    const match = description.match(new RegExp(`at least\\s+(\\d+)[^\\d]*${keyword}`, 'i'));
    return match ? Number(match[1]) : null;
};

export const breakdownPasswordDescription = (
    passwordRequirement: string[],
    currentPassword: string,
    currentComplexity: number,
    minimumComplexity: number
): {
    description: string;
    fulfilled: boolean;
}[] => {
    return passwordRequirement.map((description) => {
        let fulfilled = false;

        if (!currentPassword) return { description, fulfilled };

        // Basic checks for common rules
        if (/lowercase/i.test(description)) {
            const min = getMinFromDescription(description, 'lowercase');
            const count = countMatches(currentPassword, new RegExp(REGEX.LOWERCASE, 'g'));
            fulfilled = min ? count >= min : count > 0;
        } else if (/uppercase/i.test(description)) {
            const min = getMinFromDescription(description, 'uppercase');
            const count = countMatches(currentPassword, new RegExp(REGEX.UPPERCASE, 'g'));
            fulfilled = min ? count >= min : count > 0;
        } else if (/number/i.test(description)) {
            const min = getMinFromDescription(description, 'number');
            const count = countMatches(currentPassword, new RegExp(REGEX.NUMBER, 'g'));
            fulfilled = min ? count >= min : count > 0;
        } else if (/special/i.test(description)) {
            const min = getMinFromDescription(description, 'special');
            // Special = non-alphanumeric, excluding whitespace
            const count = countMatches(currentPassword, new RegExp(REGEX.SPECIAL, 'g'));
            fulfilled = min ? count >= min : count > 0;
        } else if (/from (\d+) to (\d+)/i.test(description)) {
            const [, min, max] = description.match(/from (\d+) to (\d+)/i)!;
            fulfilled = currentPassword.length >= Number(min) && currentPassword.length <= Number(max);
        } else if (/at least (\d+)/i.test(description)) {
            const [, min] = description.match(/at least (\d+)/i)!;
            fulfilled = currentPassword.length >= Number(min);
        } else if (/at most (\d+)/i.test(description)) {
            const [, max] = description.match(/at most (\d+)/i)!;
            fulfilled = currentPassword.length <= Number(max);
        } else if (/minimum complexity level of (\d+)/i.test(description)) {
            fulfilled = currentComplexity >= minimumComplexity;
        }

        return { description, fulfilled };
    });
};
